import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Trophy, Medal, Award, Star, Flag, X, ChevronDown, ChevronUp, Zap, Car, AlertCircle, CloudRain, Timer, CheckCircle, Eye } from 'lucide-react';
import { getLeaderboard, getYearlyBonusHolder, getFinancialSettings, getUserPredictions, getDrivers, getTeams } from '../services/api';
import { getRaceFlag } from '../utils/countryFlags';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { SprintPredictionBlock, getSprintPoints } from './SprintPredictionBlock';

const Leaderboard = ({ limit = 25 }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bonusHolder, setBonusHolder] = useState(null);
  const [bonusAmount, setBonusAmount] = useState(0);
  
  // User predictions modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPredictions, setUserPredictions] = useState([]);
  const [userChampionship, setUserChampionship] = useState(null);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [expandedModalRaces, setExpandedModalRaces] = useState({});
  const [expandedChampionship, setExpandedChampionship] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leaderboardData, bonusData, financialData, driversData, teamsData] = await Promise.all([
          getLeaderboard(limit),
          getYearlyBonusHolder(2026).catch(() => null),
          getFinancialSettings(2026).catch(() => ({ yearly_bonus: 0, total_bonus_pot: 0 })),
          getDrivers(2026).catch(() => []),
          getTeams(2026).catch(() => [])
        ]);
        setStandings(leaderboardData);
        setBonusHolder(bonusData);
        // Use total_bonus_pot (active_users × yearly_bonus) instead of just yearly_bonus per person
        setBonusAmount(financialData?.total_bonus_pot || 0);
        setDrivers(driversData);
        setTeams(teamsData);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [limit]);

  const handleUserClick = async (player) => {
    setSelectedUser(player);
    setShowModal(true);
    setLoadingPredictions(true);
    setExpandedModalRaces({}); // Reset expanded state
    setExpandedChampionship(false);
    
    try {
      const data = await getUserPredictions(player.user_id, 2026);
      // New API returns { race_predictions: [], championship_prediction: {} }
      setUserPredictions(data.race_predictions || data || []);
      setUserChampionship(data.championship_prediction || null);
    } catch (error) {
      console.error('Error fetching user predictions:', error);
      setUserPredictions([]);
      setUserChampionship(null);
    } finally {
      setLoadingPredictions(false);
    }
  };
  
  const toggleModalRaceExpand = (raceId) => {
    setExpandedModalRaces(prev => ({
      ...prev,
      [raceId]: !prev[raceId]
    }));
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.driver_id === driverId);
    return driver?.name || driverId;
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team?.name || teamId;
  };

  const getMedalIcon = (position) => {
    switch (position) {
      case 1:
        return <Trophy className="text-yellow-500" size={20} />;
      case 2:
        return <Medal className="text-gray-400" size={20} />;
      case 3:
        return <Award className="text-orange-600" size={20} />;
      default:
        return null;
    }
  };

  // Group predictions by race
  const groupPredictionsByRace = (predictions) => {
    const grouped = {};
    predictions.forEach(pred => {
      if (!grouped[pred.race_id]) {
        grouped[pred.race_id] = {
          race_id: pred.race_id,
          race_name: pred.race_name,
          race_country: pred.race_country,
          race_round: pred.race_round,
          race: null,
          qualifying: null
        };
      }
      if (pred.type === 'race') {
        grouped[pred.race_id].race = pred;
      } else if (pred.type === 'qualifying') {
        grouped[pred.race_id].qualifying = pred;
      }
    });
    return Object.values(grouped).sort((a, b) => (a.race_round || 0) - (b.race_round || 0));
  };

  if (loading) {
    return (
      <Card className="border-2 border-[#FDB913]">
        <CardHeader className="bg-[#FDB913]/10 border-b border-[#FDB913]/30">
          <CardTitle className="text-[#FDB913] flex items-center space-x-2">
            <Trophy size={24} />
            <span>STAND</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <div className="text-muted-foreground">Laden...</div>
        </CardContent>
      </Card>
    );
  }

  const groupedPredictions = groupPredictionsByRace(userPredictions);

  return (
    <div className="space-y-6">
      {/* Yearly Bonus Card */}
      {bonusAmount > 0 && (
        <Card className="border-2 border-purple-500 bg-gradient-to-r from-purple-500/10 to-purple-600/5 dark:from-purple-900/30 dark:to-purple-800/20">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-500 rounded-full p-2 sm:p-3">
                  <Star size={24} className="text-white sm:w-7 sm:h-7" fill="currentColor" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-purple-600 dark:text-purple-400">
                    Jaarbonus
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Hoogste score van het seizoen wint €{bonusAmount.toFixed(2).replace('.', ',')}
                  </p>
                </div>
              </div>
              
              {bonusHolder?.holder && (
                <div className="flex items-center space-x-4 bg-card rounded-lg px-4 py-3 border border-purple-300 dark:border-purple-700 w-full sm:w-auto">
                  <div className="text-center sm:text-left flex-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Huidige Houder</p>
                    <p className="font-bold text-foreground text-lg">{bonusHolder.holder.username}</p>
                    <div className="flex items-center justify-center sm:justify-start space-x-1 text-xs text-muted-foreground mt-1">
                      <span>{getRaceFlag(bonusHolder.race?.country)}</span>
                      <span>{bonusHolder.race?.name}</span>
                    </div>
                  </div>
                  <div className="text-center border-l border-purple-300 dark:border-purple-700 pl-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Score</p>
                    <p className="font-bold text-purple-600 dark:text-purple-400 text-2xl">{bonusHolder.score}</p>
                    <p className="text-xs text-muted-foreground">punten</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard Card */}
      <Card className="border-2 border-[#FDB913]">
        <CardHeader className="bg-[#FDB913]/10 border-b border-[#FDB913]/30 px-3 sm:px-6">
          <CardTitle className="text-[#FDB913] flex items-center space-x-2 text-base sm:text-lg">
            <Trophy size={20} className="sm:w-6 sm:h-6" />
            <span>STAND{limit > 0 ? ` (Top ${limit})` : ` (${standings.length} deelnemers)`}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-secondary">
              <tr>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-muted-foreground uppercase w-16 sm:w-auto">#</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Speler</th>
                <th className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-semibold text-muted-foreground uppercase">Voorsp.</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-semibold text-muted-foreground uppercase w-16 sm:w-auto">Punten</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-semibold text-muted-foreground uppercase w-16">Bekijk</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((player, index) => {
                const isBonusHolder = bonusHolder?.holder?.user_id === player.user_id;
                return (
                  <tr
                    key={player.user_id}
                    className={`transition-colors cursor-pointer ${
                      index % 2 === 0 ? 'bg-card' : 'bg-secondary/50'
                    } hover:bg-[#FDB913]/10 border-b border-border ${
                      isBonusHolder ? 'bg-purple-100/50 dark:bg-purple-900/20' : ''
                    }`}
                    onClick={() => handleUserClick(player)}
                  >
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        {getMedalIcon(player.position)}
                        <span
                          className={`font-bold text-sm sm:text-base ${
                            player.position <= 3 ? 'text-[#FDB913]' : 'text-muted-foreground'
                          }`}
                        >
                          {player.position}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-foreground text-sm sm:text-base truncate block max-w-[120px] sm:max-w-none">
                          {player.username}
                        </span>
                        {isBonusHolder && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                            <Star size={10} className="mr-0.5" fill="currentColor" />
                            Jaarbonus
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-2 sm:px-4 py-2 sm:py-3 text-center">
                      <span className="text-muted-foreground text-sm">{player.predictions}</span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                      <span className="font-bold text-[#FDB913] text-sm sm:text-base">{player.points}</span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                      <Button variant="ghost" size="sm" className="text-[#FDB913] hover:bg-[#FDB913]/10" data-testid={`view-predictions-${player.user_id}`}>
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* User Predictions Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-predictions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-[#FDB913]">
              <Eye size={24} />
              <span>Voorspellingen van {selectedUser?.username}</span>
            </DialogTitle>
            <DialogDescription>
              Positie #{selectedUser?.position} • {selectedUser?.points} punten • {selectedUser?.predictions} voorspellingen
            </DialogDescription>
          </DialogHeader>
          
          {loadingPredictions ? (
            <div className="p-8 text-center text-muted-foreground">
              Voorspellingen laden...
            </div>
          ) : groupedPredictions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Geen voorspellingen gevonden
            </div>
          ) : (
            <div className="space-y-2 mt-4">
              {/* Championship Prediction Section */}
              {userChampionship && (
                <div className="border-2 border-purple-500/50 rounded-lg overflow-hidden mb-4">
                  {/* Championship Header */}
                  <div 
                    className="bg-gradient-to-r from-purple-500/20 to-purple-600/10 px-4 py-3 flex items-center justify-between cursor-pointer hover:from-purple-500/30"
                    onClick={() => setExpandedChampionship(!expandedChampionship)}
                  >
                    <span className="flex items-center space-x-2">
                      <Trophy size={18} className="text-purple-500" />
                      <span className="font-bold text-purple-400">Kampioenschap Voorspelling</span>
                      {userChampionship.is_scored && (
                        <span className="ml-2 px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Gescoord</span>
                      )}
                    </span>
                    <div className="flex items-center space-x-3">
                      <span className="font-bold text-purple-400">
                        {userChampionship.score || 0} pts
                      </span>
                      {expandedChampionship ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>
                  
                  {/* Championship Details */}
                  {expandedChampionship && (
                    <div className="p-4 bg-[#1a2332]/50 space-y-4">
                      {/* World Champion Top 3 */}
                      {userChampionship.world_champion_top3?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-purple-400 mb-2">🏆 Wereldkampioen Top 3</h4>
                          <div className="space-y-1">
                            {userChampionship.world_champion_top3.map((driverId, idx) => {
                              const wcBreakdown = userChampionship.score_breakdown?.world_champion?.[idx];
                              const points = wcBreakdown?.points || 0;
                              return (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-purple-400 font-bold w-6">
                                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                                    </span>
                                    <span className="text-white">{getDriverName(driverId)}</span>
                                  </div>
                                  {userChampionship.is_scored && (
                                    <span className={`text-xs px-2 py-0.5 rounded ${points > 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                      +{points}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Constructor Champion Top 3 */}
                      {userChampionship.constructor_champion_top3?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-red-400 mb-2">🏎️ Constructeurskampioen Top 3</h4>
                          <div className="space-y-1">
                            {userChampionship.constructor_champion_top3.map((teamId, idx) => {
                              const ccBreakdown = userChampionship.score_breakdown?.constructor_champion?.[idx];
                              const points = ccBreakdown?.points || 0;
                              return (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-red-400 font-bold w-6">
                                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                                    </span>
                                    <span className="text-white">{getTeamName(teamId)}</span>
                                  </div>
                                  {userChampionship.is_scored && (
                                    <span className={`text-xs px-2 py-0.5 rounded ${points > 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                      +{points}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Expand/Collapse buttons */}
              <div className="flex justify-end gap-2 mb-2">
                <button 
                  onClick={() => {
                    const allExpanded = {};
                    groupedPredictions.forEach(r => { allExpanded[r.race_id] = true; });
                    setExpandedModalRaces(allExpanded);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-secondary"
                >
                  Alles uitklappen
                </button>
                <span className="text-muted-foreground">|</span>
                <button 
                  onClick={() => setExpandedModalRaces({})}
                  className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-secondary"
                >
                  Alles inklappen
                </button>
              </div>
              
              {groupedPredictions.map((raceData) => {
                const raceScore = raceData.race?.points_earned || 0;
                const sprintScore = getSprintPoints(raceData.race?.score_breakdown);
                const qualScore = raceData.qualifying?.points_earned || 0;
                const totalScore = raceScore + qualScore;
                const isExpanded = expandedModalRaces[raceData.race_id];
                
                return (
                  <div key={raceData.race_id} className="border border-[#FDB913]/30 rounded-lg overflow-hidden">
                    {/* Compact Header - Always visible */}
                    <div 
                      className="bg-gradient-to-r from-[#FDB913]/10 to-transparent px-4 py-2 flex items-center justify-between cursor-pointer hover:from-[#FDB913]/20"
                      onClick={() => toggleModalRaceExpand(raceData.race_id)}
                    >
                      <span className="flex items-center space-x-2">
                        <span>{getRaceFlag(raceData.race_country)}</span>
                        <span className="text-foreground font-medium text-sm">{raceData.race_name}</span>
                      </span>
                      <div className="flex items-center space-x-2">
                        {totalScore > 0 && (
                          <span className="bg-[#FDB913] text-[#1a2332] px-2 py-0.5 rounded-full text-xs font-bold">
                            {totalScore} pts
                          </span>
                        )}
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>
                    
                    {/* Expandable Content */}
                    {isExpanded && (
                    <div className="p-3 bg-card">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Qualifying Prediction */}
                        {raceData.qualifying && (
                          <div className="bg-[#1a2332] border border-blue-500/50 rounded p-2">
                            <h4 className="font-semibold text-white mb-2 flex items-center justify-between text-xs">
                              <span className="flex items-center">
                                <CheckCircle className="mr-1 text-green-500" size={12} />
                                Kwalificatie
                              </span>
                              {qualScore > 0 && (
                                <span className="text-xs bg-blue-500/30 text-blue-300 px-1.5 py-0.5 rounded">
                                  +{qualScore}
                                </span>
                              )}
                            </h4>
                            <div className="space-y-1 text-xs">
                              {raceData.qualifying.qualifying?.map((driverId, i) => {
                                const posPoints = raceData.qualifying.score_breakdown?.[`Q${i+1}_exact`] || 0;
                                const isScored = raceData.qualifying.is_scored;
                                return (
                                  <div key={i} className="flex items-center justify-between" data-testid={`qual-driver-p${i+1}`}>
                                    <div className="flex items-center space-x-1">
                                      <span className="font-bold text-blue-400 w-6">P{i + 1}:</span>
                                      <span className="text-white">{getDriverName(driverId)}</span>
                                    </div>
                                    {isScored && (
                                      <span className={`text-xs px-1 py-0.5 rounded ${posPoints > 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>+{posPoints}</span>
                                    )}
                                  </div>
                                );
                              })}
                              {raceData.qualifying.pole_time && (
                                <div className="mt-1.5 pt-1.5 border-t border-blue-500/30 flex items-center justify-between">
                                  <div className="flex items-center">
                                    <Timer size={10} className="mr-1 text-blue-400" />
                                    <span className="text-gray-400">Pole:</span>
                                    <span className="text-white ml-1">{raceData.qualifying.pole_time}</span>
                                  </div>
                                  {raceData.qualifying.is_scored && (() => {
                                    // Check both regular pole_time points AND pole_time_fallback bonus
                                    const poleTimePoints = raceData.qualifying.score_breakdown?.pole_time?.points || raceData.qualifying.score_breakdown?.pole_time || 0;
                                    const poleFallbackPoints = raceData.qualifying.score_breakdown?.pole_time_fallback?.points || 0;
                                    const totalPolePoints = poleTimePoints + poleFallbackPoints;
                                    return (
                                      <span className={`text-xs px-1 py-0.5 rounded ${totalPolePoints > 0 ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                        +{totalPolePoints}
                                      </span>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Race Prediction */}
                        {raceData.race && (
                          <div className="bg-[#1a2332] border border-[#FDB913]/50 rounded p-2">
                            <h4 className="font-semibold text-[#FDB913] mb-2 flex items-center justify-between text-xs">
                              <span className="flex items-center">
                                <CheckCircle className="mr-1 text-green-500" size={12} />
                                Race Top 5
                              </span>
                              {raceScore - sprintScore > 0 && (
                                <span className="text-xs bg-[#FDB913]/30 text-[#FDB913] px-1.5 py-0.5 rounded">
                                  +{raceScore - sprintScore}
                                </span>
                              )}
                            </h4>
                            <div className="space-y-1 text-xs">
                              {raceData.race.top5?.map((driverId, i) => {
                                const breakdownKey = `P${i+1}_${driverId}`;
                                const posBreakdown = raceData.race.score_breakdown?.[breakdownKey];
                                const posPoints = posBreakdown?.points || 0;
                                const actualPos = posBreakdown?.actual;
                                const isScored = raceData.race.is_scored;
                                return (
                                  <div key={i} className="flex items-center justify-between" data-testid={`race-driver-p${i+1}`}>
                                    <div className="flex items-center space-x-1">
                                      <span className="font-bold text-[#FDB913] w-6">P{i + 1}:</span>
                                      <span className="text-white">{getDriverName(driverId)}</span>
                                      {isScored && actualPos != null && actualPos !== (i + 1) && (
                                        <span className="text-gray-500 text-[10px]">(werd P{actualPos})</span>
                                      )}
                                    </div>
                                    {isScored && (
                                      <span className={`text-xs px-1 py-0.5 rounded ${posPoints > 0 ? 'bg-[#FDB913]/20 text-[#FDB913]' : 'bg-gray-500/20 text-gray-400'}`}>+{posPoints}</span>
                                    )}
                                  </div>
                                );
                              })}
                              {/* Extra race predictions in compact form */}
                              <div className="mt-1.5 pt-1.5 border-t border-[#FDB913]/30 grid grid-cols-2 gap-1 text-xs">
                                {raceData.race.fastest_lap && (
                                  <div className="flex items-center justify-between col-span-2">
                                    <span className="text-gray-400 flex items-center"><Zap size={10} className="mr-1" /> Snelste: {getDriverName(raceData.race.fastest_lap)}</span>
                                    {raceData.race.is_scored && <span className={`px-1 rounded ${raceData.race.score_breakdown?.fastest_lap > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>+{raceData.race.score_breakdown?.fastest_lap || 0}</span>}
                                  </div>
                                )}
                                {raceData.race.safety_car && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-400">SC: {raceData.race.safety_car === 'ja' ? 'Ja' : 'Nee'}</span>
                                    {raceData.race.is_scored && <span className={`px-1 rounded ${raceData.race.score_breakdown?.safety_car > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>+{raceData.race.score_breakdown?.safety_car || 0}</span>}
                                  </div>
                                )}
                                {raceData.race.virtual_safety_car && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-400">VSC: {raceData.race.virtual_safety_car === 'ja' ? 'Ja' : 'Nee'}</span>
                                    {raceData.race.is_scored && <span className={`px-1 rounded ${raceData.race.score_breakdown?.virtual_safety_car > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>+{raceData.race.score_breakdown?.virtual_safety_car || 0}</span>}
                                  </div>
                                )}
                                {raceData.race.dnf_count != null && raceData.race.dnf_count !== '' && (
                                  <div className="flex items-center justify-between" data-testid="race-dnf-row">
                                    <span className="text-gray-400">DNF: {raceData.race.dnf_count}</span>
                                    {raceData.race.is_scored && <span className={`px-1 rounded ${raceData.race.score_breakdown?.dnf_count > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>+{raceData.race.score_breakdown?.dnf_count || 0}</span>}
                                  </div>
                                )}
                                {raceData.race.rain_race && (
                                  <div className="flex items-center justify-between" data-testid="race-rain-row">
                                    <span className="text-gray-400">Regen: {raceData.race.rain_race === 'ja' ? 'Ja' : 'Nee'}</span>
                                    {raceData.race.is_scored && <span className={`px-1 rounded ${raceData.race.score_breakdown?.rain_race > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>+{raceData.race.score_breakdown?.rain_race || 0}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {raceData.race && (
                          <SprintPredictionBlock prediction={raceData.race} getDriverName={getDriverName} />
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leaderboard;
